import {
	closestRequired, currentTargetRequired,
	getAttributeOrThrow,
	qsaRequired,
	qsOptional,
	qsRequired,
} from '@/scripts/core/global'
import { removeTrapFocus, trapFocus } from '@/scripts/core/global'
import { UcoastEl } from '@/scripts/core/UcoastEl'
import { getSectionHTMLForResource } from '@/scripts/core/cart-functions'
import { UgcSlider } from '@/scripts/theme/ugc-slider'
import { UcoastVideo } from '@/scripts/core/ucoast-video'

export class UgcPopup extends UcoastEl {
	static htmlSelector = 'ugc-popup'
	dynamicContent: HTMLElement
	openedBy?: HTMLElement
	navButtons: NodeListOf<HTMLButtonElement>
	pauseButton: HTMLButtonElement
	muteButton: HTMLButtonElement
	constructor() {
		super()
		this.querySelectorAll('[id^="ModalClose-"]')?.forEach((closeButton) => {
			closeButton.addEventListener('click', this.hide.bind(this, false))
		})
		this.addEventListener('keyup', (event) => {
			if (event.code?.toUpperCase() === 'ESCAPE') this.hide()
		})
		this.addEventListener('click', (event) => {
			if (event.target === this) this.hide()
		})
		this.dynamicContent = qsRequired('[data-dynamic-content]', this)
		this.pauseButton = qsRequired('[data-ugc-media="pause"]', this)
		this.pauseButton.addEventListener('click', this.togglePlay.bind(this))
		this.muteButton = qsRequired('[data-ugc-media="mute"]', this)
		this.muteButton.addEventListener('click', this.toggleMute.bind(this))
		this.navButtons = qsaRequired('[data-ugc-nav]', this)
		this.navButtons.forEach(el => {
			el.addEventListener('click', this.onNavClick.bind(this))
		})
	}

	override connectedCallback() {

	}

	getVideoEl() {
		return qsOptional<UcoastVideo>('ucoast-video', this)
	}

	togglePlay() {
		const video = this.getVideoEl()
		if (!video) return
		const paused = video.videoEl.paused
		if (paused) {
			void video.playInPlace()
		} else {
			void video.pauseInPlace()
		}
	}

	pauseOnClose() {
		const video = this.getVideoEl()
		if (!video) return
		const paused = video.videoEl.paused
		if (!paused) {
			void video.pauseInPlace()
		}
	}

	onNavClick(event: Event) {
		const button = currentTargetRequired(event)
		const direction = getAttributeOrThrow('data-ugc-nav', button)
		void this.reloadFromNav()
		const slider = this.getUgcSlider()
		if (!slider) return
		if (direction === 'next') {
			slider.emblaApi?.scrollNext()
			//const canLoop = slider.emblaApi?.internalEngine().slideLooper.canLoop() // voodoo
			//const loopPoints = slider.emblaApi?.internalEngine().slideLooper.loopPoints // voodoo
			//slider.emblaApi?.internalEngine().slideLooper.clear()
			//slider.emblaApi?.internalEngine().scrollLooper.loop(-1)
		}
		if (direction === 'prev') {
			slider.emblaApi?.scrollPrev()
			//const canLoop = slider.emblaApi?.internalEngine().slideLooper.canLoop() // voodoo
			//const loopPoints = slider.emblaApi?.internalEngine().slideLooper.loopPoints // voodoo
			//slider.emblaApi?.internalEngine().slideLooper.loop()
			//slider.emblaApi?.internalEngine().scrollLooper.loop(1)
		}


		//this.getUgcSlider()?.pauseAllSlides()

	}

	toggleMute() {
		const video = this.getVideoEl()
		if (!video) return
		const muted = video.videoEl.muted
		video.videoEl.muted = !muted;
	}

	getHtmlSelector() {
		return UgcPopup.htmlSelector
	}

	isInline(): boolean {
		return false
	}

	async show(opener: HTMLElement, sourceUrl: string) {
		const html = await getSectionHTMLForResource(sourceUrl, 'dynamic-ugc-popup')
		if (!html) {
			throw new Error(`html not found for sourceUrl: ${sourceUrl}`)
		}
		this.dynamicContent.innerHTML = html;
		this.openedBy = opener
		document.body.classList.add('overflow-hidden')
		this.setAttribute('open', '')
		const dialog = qsRequired('[role="dialog"]', this)
		trapFocus(this, dialog)
		void window.Ucoast.mediaManager.loadAllInContainer(dialog)
		this.setDynamicListeners()
	}

	async reloadFromNav() {
		const details = this.getUgcSlider()?.getCurrentSlideDetails()
		const dialog = qsRequired('[role="dialog"]', this)
		this.dynamicContent.classList.add('loading')
		if (!details?.cta || !details.url) {
			this.dynamicContent.classList.remove('loading')
			throw new Error('cannot find slide to reload from')
		}
		const html = await getSectionHTMLForResource(details.url, 'dynamic-ugc-popup')
		if (!html) {
			this.dynamicContent.classList.remove('loading')
			throw new Error(`html not found for sourceUrl: ${details.url}`)
		}
		this.openedBy?.setAttribute('aria-expanded', 'false')
		this.dynamicContent.innerHTML = html;
		this.openedBy = details.cta
		this.openedBy.setAttribute('aria-expanded', 'true')
		window.Ucoast.mediaManager.loadAllInContainer(dialog).then(() => {
			this.dynamicContent.classList.remove('loading')
		})
		document.body.classList.add('overflow-hidden')
		this.setDynamicListeners()

	}

	hide(preventFocus = false) {
		if (preventFocus) this.openedBy = undefined
		document.body.classList.remove('overflow-hidden')
		document.body.dispatchEvent(new CustomEvent('modalClosed'))
		this.removeAttribute('open')
		removeTrapFocus(this.openedBy)
		this.getUgcSlider()?.playSlidesInView()
		this.pauseOnClose()
	}
	getUgcSlider() {
		if (!this.openedBy) return
		return closestRequired<UgcSlider>(this.openedBy, 'ugc-slider')
	}
	setDynamicListeners() {
		this.getMobileProductsToggle().addEventListener('click', this.toggleProductsVisible.bind(this))
	}
	toggleProductsVisible() {
		const toggleButton = this.getMobileProductsToggle();
		const scroller = this.getMobileScroller()
		if (this.getMobileProductsToggle().getAttribute('aria-expanded') === 'false') {
			scroller.classList.add('active')
			toggleButton.setAttribute('aria-expanded', 'true')
			void window.Ucoast.mediaManager.loadAllInContainer(scroller)
		} else {
			scroller.classList.remove('active')
			toggleButton.setAttribute('aria-expanded', 'false')
		}
	}
	getMobileProductsToggle() {
		return qsRequired<HTMLButtonElement>('[data-mobile-toggle]', this.dynamicContent)
	}
	getMobileScroller() {
		return qsRequired('[data-products-scroller]', this.dynamicContent)
	}
}
